import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SeoScript {
  id: string;
  name: string;
  script_type: string;
  placement: string;
  script_content: string;
  load_order: number;
}

// Allowed script patterns for security validation
const ALLOWED_SCRIPT_PATTERNS = [
  // Google Tag Manager
  /^\s*\(function\s*\(\s*w\s*,\s*d\s*,\s*s\s*,\s*l\s*,\s*i\s*\)/,
  /googletagmanager\.com/,
  // Google Analytics
  /google-analytics\.com/,
  /gtag\s*\(/,
  // Meta/Facebook Pixel
  /connect\.facebook\.net/,
  /fbq\s*\(/,
  // LinkedIn Insight
  /snap\.licdn\.com/,
  // Twitter/X Pixel
  /platform\.twitter\.com/,
  // Hotjar
  /static\.hotjar\.com/,
  // Clarity
  /clarity\.ms/,
];

// Dangerous patterns to block
const DANGEROUS_PATTERNS = [
  /onerror\s*=/i,
  /onload\s*=/i,
  /onclick\s*=/i,
  /onmouseover\s*=/i,
  /onfocus\s*=/i,
  /onblur\s*=/i,
  /javascript\s*:/i,
  /data\s*:/i,
  /document\.cookie/i,
  /localStorage/i,
  /sessionStorage/i,
  /eval\s*\(/i,
  /Function\s*\(/i,
  /setTimeout\s*\(\s*["']/i,
  /setInterval\s*\(\s*["']/i,
];

/**
 * Validates script content for security.
 * Only allows known analytics/tracking scripts.
 */
function isScriptSafe(content: string, scriptType: string): boolean {
  // Check for dangerous patterns first
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(content)) {
      console.warn(`[GlobalScripts] Blocked script with dangerous pattern: ${pattern}`);
      return false;
    }
  }

  // For known script types, verify they match expected patterns
  const knownTypes = ['gtm', 'google_analytics', 'meta_pixel', 'hotjar', 'clarity', 'linkedin', 'twitter'];
  
  if (knownTypes.includes(scriptType.toLowerCase())) {
    // Known types should match at least one allowed pattern
    const hasAllowedPattern = ALLOWED_SCRIPT_PATTERNS.some(pattern => pattern.test(content));
    if (!hasAllowedPattern) {
      console.warn(`[GlobalScripts] Script type ${scriptType} doesn't match expected patterns`);
      return false;
    }
  }

  // For custom scripts, be more restrictive - only allow if they match known safe patterns
  if (scriptType.toLowerCase() === 'custom') {
    const hasAllowedPattern = ALLOWED_SCRIPT_PATTERNS.some(pattern => pattern.test(content));
    if (!hasAllowedPattern) {
      console.warn(`[GlobalScripts] Custom script blocked - doesn't match any allowed patterns`);
      return false;
    }
  }

  return true;
}

/**
 * Safely inject script content by creating proper script elements
 * instead of using innerHTML
 */
function createSafeScriptElement(content: string): HTMLScriptElement | null {
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  
  // Extract src if it's an external script tag
  const srcMatch = content.match(/src\s*=\s*["']([^"']+)["']/i);
  if (srcMatch && srcMatch[1]) {
    // External script - only allow trusted domains
    const trustedDomains = [
      'googletagmanager.com',
      'google-analytics.com',
      'connect.facebook.net',
      'snap.licdn.com',
      'platform.twitter.com',
      'static.hotjar.com',
      'clarity.ms',
      'www.googletagmanager.com',
      'www.google-analytics.com',
    ];
    
    try {
      const url = new URL(srcMatch[1]);
      const isTrusted = trustedDomains.some(domain => url.hostname.endsWith(domain));
      if (!isTrusted) {
        console.warn(`[GlobalScripts] Blocked external script from untrusted domain: ${url.hostname}`);
        return null;
      }
      scriptElement.src = srcMatch[1];
      scriptElement.async = true;
    } catch {
      console.warn(`[GlobalScripts] Invalid script URL: ${srcMatch[1]}`);
      return null;
    }
  } else {
    // Inline script - extract just the script content
    const inlineMatch = content.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
    if (inlineMatch && inlineMatch[1]) {
      scriptElement.textContent = inlineMatch[1].trim();
    } else {
      // Content is already just the script body
      scriptElement.textContent = content.trim();
    }
  }
  
  return scriptElement;
}

// Determine current environment
function getCurrentEnvironment(): string {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "development";
  }
  if (hostname.includes("preview") || hostname.includes("staging")) {
    return "staging";
  }
  return "production";
}

export function GlobalScripts() {
  const currentEnv = getCurrentEnvironment();
  const bodyStartContainerRef = useRef<HTMLDivElement | null>(null);
  const bodyEndContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Defer loading until after initial render to avoid blocking critical path
  const [shouldFetch, setShouldFetch] = useState(false);
  
  useEffect(() => {
    // Defer script loading until after LCP to avoid blocking critical path
    // Use requestIdleCallback with longer timeout for better LCP scores
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => setShouldFetch(true), { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    } else {
      // Fallback: wait longer to avoid impacting initial render
      const timeout = setTimeout(() => setShouldFetch(true), 2000);
      return () => clearTimeout(timeout);
    }
  }, []);

  const { data: scripts } = useQuery({
    queryKey: ["seo-scripts", currentEnv],
    queryFn: async (): Promise<SeoScript[]> => {
      // Scripts require admin access, so we use edge function
      const { data: session } = await supabase.auth.getSession();
      
      // For non-admin users, we fetch via a public endpoint or cache
      // For now, scripts are only loaded for the current environment
      const { data, error } = await supabase
        .from("seo_scripts")
        .select("id, name, script_type, placement, script_content, load_order")
        .eq("is_active", true)
        .contains("environment", [currentEnv])
        .order("load_order", { ascending: true });

      if (error) {
        // If RLS blocks (non-admin), return empty - scripts won't load
        // This is expected for non-admin users
        console.log("Scripts not available (expected for non-admin users)");
        return [];
      }

      return data || [];
    },
    enabled: shouldFetch, // Only fetch after page becomes idle
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: false
  });

  const headScripts = scripts?.filter(s => s.placement === "head") || [];
  const bodyStartScripts = scripts?.filter(s => s.placement === "body_start") || [];
  const bodyEndScripts = scripts?.filter(s => s.placement === "body_end") || [];

  // Safely inject body_start scripts
  useEffect(() => {
    if (bodyStartScripts.length === 0) return;

    // Clean up existing container
    const existingContainer = document.getElementById("seo-body-start-scripts");
    if (existingContainer) existingContainer.remove();

    const container = document.createElement("div");
    container.id = "seo-body-start-scripts";
    
    bodyStartScripts.forEach(script => {
      // Validate script before injection
      if (!isScriptSafe(script.script_content, script.script_type)) {
        console.error(`[GlobalScripts] Blocked unsafe script: ${script.name}`);
        return;
      }
      
      const scriptElement = createSafeScriptElement(script.script_content);
      if (scriptElement) {
        container.appendChild(scriptElement);
      }
    });

    const firstChild = document.body.firstChild;
    if (firstChild) {
      document.body.insertBefore(container, firstChild);
    } else {
      document.body.appendChild(container);
    }

    bodyStartContainerRef.current = container;

    return () => {
      const el = document.getElementById("seo-body-start-scripts");
      if (el) el.remove();
    };
  }, [bodyStartScripts]);

  // Safely inject body_end scripts
  useEffect(() => {
    if (bodyEndScripts.length === 0) return;

    // Clean up existing container
    const existingContainer = document.getElementById("seo-body-end-scripts");
    if (existingContainer) existingContainer.remove();

    const container = document.createElement("div");
    container.id = "seo-body-end-scripts";
    
    bodyEndScripts.forEach(script => {
      // Validate script before injection
      if (!isScriptSafe(script.script_content, script.script_type)) {
        console.error(`[GlobalScripts] Blocked unsafe script: ${script.name}`);
        return;
      }
      
      const scriptElement = createSafeScriptElement(script.script_content);
      if (scriptElement) {
        container.appendChild(scriptElement);
      }
    });

    document.body.appendChild(container);

    bodyEndContainerRef.current = container;

    return () => {
      const el = document.getElementById("seo-body-end-scripts");
      if (el) el.remove();
    };
  }, [bodyEndScripts]);

  // Filter head scripts for safety
  const safeHeadScripts = headScripts.filter(script => 
    isScriptSafe(script.script_content, script.script_type)
  );

  if (safeHeadScripts.length === 0) {
    return null;
  }

  return (
    <Helmet>
      {safeHeadScripts.map(script => {
        // Extract external src if present
        const srcMatch = script.script_content.match(/src\s*=\s*["']([^"']+)["']/i);
        if (srcMatch && srcMatch[1]) {
          return (
            <script key={script.id} type="text/javascript" src={srcMatch[1]} async />
          );
        }
        
        // Inline script - extract content safely
        const inlineMatch = script.script_content.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
        const scriptBody = inlineMatch ? inlineMatch[1].trim() : script.script_content.trim();
        
        return (
          <script key={script.id} type="text/javascript">
            {scriptBody}
          </script>
        );
      })}
    </Helmet>
  );
}
